import styled from '@emotion/styled'
import * as Popover from '@radix-ui/react-popover'
import { Command } from 'cmdk'
import { Key, useMemo, useState } from 'react'

import { Color, theme } from '../../constants'
import { useTranslations } from '../../translate'
import { Icon } from '../Icon'
import { FilterInput } from '../Menu/PopoverCombobox/FilterInput'
import { PopoverComboboxOptions } from '../Menu/PopoverCombobox/Options'
import { panelStyles } from '../Menu/styles'

const Panel = styled(Command)(panelStyles)

const PopoverContent = styled(Popover.Content)`
  width: var(--radix-popover-trigger-width);
  z-index: ${theme.zIndex.popover};
`

const Trigger = styled.button<{ empty: boolean }>(({ empty }) => ({
  background: Color.LIGHT_GREY,
  color: empty ? Color.DARK_GREY : 'inherit',
  border: theme.border.style.thinMedium,
  borderRadius: theme.border.radius.normal,
  boxSizing: 'border-box',
  cursor: 'pointer',
  fontFamily: 'inherit',
  lineHeight: `${theme.elements.icon.size.sm}px`,
  padding: `${theme.spacing['2']}px ${theme.spacing['2.5']}px`,
  textAlign: 'left',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing['3'],
  width: '100%',

  '&:disabled': {
    color: Color.DARK_MEDIUM_GREY,
    cursor: 'not-allowed',
  },
}))

type ComboboxRadixOption<T> = {
  key: Key
  label: string
  value: T
}

type ComboboxRadixProps<T extends string | number> = {
  options: ComboboxRadixOption<T>[]
  value: T | undefined
  placeholder?: string
  onChange: (value: T | undefined) => void
  clearable?: boolean
  showFilter?: boolean
  disabled?: boolean
}

export const ComboboxRadix = <T extends string | number>({
  options,
  value,
  placeholder,
  onChange,
  clearable,
  showFilter,
  disabled,
}: ComboboxRadixProps<T>) => {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(false)

  const selected = useMemo(() => {
    return options.find((option) => option.value === value)
  }, [value])

  const groups = useMemo(() => {
    return [
      {
        items: options,
      },
    ]
  }, [options])

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <Trigger empty={!selected} disabled={disabled}>
          {selected
            ? selected.label
            : (placeholder ?? `${t.form.select_a_value()}...`)}
          <Icon name="ChevronDownIcon" size="sm" />
        </Trigger>
      </Popover.Trigger>

      <Popover.Portal>
        <PopoverContent sideOffset={4}>
          <Panel>
            {showFilter && (
              <FilterInput autoFocus placeholder={t.ui.filter_ellipsis()} />
            )}
            <PopoverComboboxOptions
              groups={groups}
              onSelect={(value) => {
                setIsOpen(false)

                if (clearable && value === selected?.value) {
                  onChange(undefined)
                } else {
                  onChange(value)
                }
              }}
              type="singleSelect"
              value={value}
            />
          </Panel>
        </PopoverContent>
      </Popover.Portal>
    </Popover.Root>
  )
}
